import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { languageNames, isRTL } from '../i18n';

const LanguageSwitcher = ({ variant = 'default' }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Available languages (only show implemented ones for now)
  const availableLanguages = ['en', 'da'];
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    
    // Handle RTL direction
    if (isRTL(lng)) {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = lng;
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = lng;
    }
    
    setIsOpen(false);
  };
  
  const currentLanguage = i18n.language?.split('-')[0] || 'en';
  
  if (variant === 'minimal') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors text-sm"
          aria-label="Change language"
        >
          <Globe className="w-4 h-4" />
          <span className="uppercase">{currentLanguage}</span>
        </button>
        
        {isOpen && (
          <div className="absolute bottom-full left-0 mb-2 py-1 w-32 bg-[#0a0b14] border border-white/10 rounded-lg shadow-xl z-50">
            {availableLanguages.map((lng) => (
              <button
                key={lng}
                onClick={() => changeLanguage(lng)}
                className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-white/5 transition-colors ${
                  currentLanguage === lng ? 'text-cyan-400' : 'text-gray-300'
                }`}
              >
                <span>{languageNames[lng]}</span>
                {currentLanguage === lng && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:border-white/20 transition-colors text-sm"
        aria-label="Change language"
      >
        <Globe className="w-4 h-4" />
        <span>{languageNames[currentLanguage]}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 py-1 w-40 bg-[#0a0b14] border border-white/10 rounded-lg shadow-xl z-50">
          {availableLanguages.map((lng) => (
            <button
              key={lng}
              onClick={() => changeLanguage(lng)}
              className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-white/5 transition-colors ${
                currentLanguage === lng ? 'text-cyan-400' : 'text-gray-300'
              }`}
            >
              <span>{languageNames[lng]}</span>
              {currentLanguage === lng && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
